import { useOpacityToggle } from '@hooks'
import { useConfigurationStore, useInfoLineStore, useJackpotStore, useFreespinsStore } from '@stores'
import { buildAtlasImageStyles } from '@utils/buildAtlasImageStyles'
import { formatMoney } from '@utils/money'
import {
    AtlasMapValue,
    ChangeOpacityComponents,
    BaseGameType,
    GameUIMessageScope,
    GameUIMessageType,
    InfoLineAtlasMapMessage,
    InfoLineLayoutElements,
    InfoLineSendWinningCombinationMessage,
    InfoLineUpdateFreespinCountMessage,
    SetupInfoLineLayoutMessage,
    ShowInfoLineTextMessage,
    SpinButtonState,
    ViewportType,
    WinLineCombinationData,
} from 'game-ui-types'
import { fadeGSAPAnimation } from '@utils/misc'
import { TranslationKey, useTranslation } from '@amusnet/translations/react'
import { translate } from '@amusnet/translations'

import React, { useEffect, useRef, useState } from 'react'

const InfoLine: React.FC = () => {
    const { t } = useTranslation()
    const isJackpotStarted = useJackpotStore((state) => state.isJackpotStarted)
    const isJackpotRoundRunning = useJackpotStore((state) => state.isJackpotRoundRunning)
    const isJackpotEQRunning = useJackpotStore((state) => state.isJackpotEQStarted)

    const isTextVisible = useInfoLineStore((state) => state.isTextVisible)
    const text = useInfoLineStore((state) => state.text)
    const winLineData = useInfoLineStore((state) => state.winLineData)
    const winLineCombosArray = useInfoLineStore((state) => state.winLineCombosArray)
    const freespinText = useInfoLineStore((state) => state.freespinText)
    const findElementByImageName = useInfoLineStore((state) => state.findElementByImageName)
    const cachedImages = useInfoLineStore((state) => state.cachedImages)
    const addAtlasMap = useInfoLineStore((state) => state.addAtlasMap)
    const updateTextVisibility = useInfoLineStore((state) => state.updateTextVisibility)
    const updateText = useInfoLineStore((state) => state.updateText)
    const updateWinLineData = useInfoLineStore((state) => state.updateWinLineData)
    const updateFreespinText = useInfoLineStore((state) => state.updateFreespinText)
    const preloadImages = useInfoLineStore((state) => state.preloadImages)
    const handleAtlasTexturesData = useInfoLineStore((state) => state.handleAtlasTexturesData)

    const currentFreespin = useFreespinsStore((state) => state.currentFreespin)
    const totalFreespins = useFreespinsStore((state) => state.totalFreespins)
    const updateCurrentFreespin = useFreespinsStore((state) => state.updateCurrentFreespin)
    const updateTotalFreespins = useFreespinsStore((state) => state.updateTotalFreespins)
    const setIsFreespinsActive = useFreespinsStore((state) => state.setIsFreespinsActive)

    const viewport = useConfigurationStore((state) => state.viewport)
    const currency = useConfigurationStore((state) => state.currency)
    const shouldHideTotalFreespinsText = useConfigurationStore((state) => state.shouldHideTotalFreespinsText)
    const gameType = useConfigurationStore((state) => state.gameType)
    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)

    const hasWinLineData = winLineData && Object.keys(winLineData).length > 0
    const hasMultipleWinLines =
        hasWinLineData && winLineData.extraCombinations && winLineData.extraCombinations?.length > 0
    const keepWinLinesSeparate = hasMultipleWinLines && winLineData.extraCombinations!.length === 1 // Show only up to two separated win lines, otherwise accumulate win amount

    const fitInfoInOneLine =
        viewport === ViewportType.DESKTOP ||
        viewport === ViewportType.MOBILE_LANDSCAPE ||
        gameType === BaseGameType.SLOT_CONFIG_C ||
        gameType === BaseGameType.SLOT_CONFIG_D ||
        gameType === BaseGameType.SLOT_CONFIG_E

    const [isFreespinFullscreen, setIsFreespinFullscreen] = useState(false)
    const hideInfoLine = isFreespinFullscreen || (!isTextVisible && !hasWinLineData && !currentFreespin)

    const position = useConfigurationStore((state) => state.getComponentConfig('InfoLine').position)
    const styles = useConfigurationStore((state) => state.getComponentConfig('InfoLine').styles)
    const infoLineJackpotConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('InfoLineJackpot').position
    )
    const infoLinePosition =
        (isJackpotStarted || isJackpotRoundRunning) && infoLineJackpotConfigPosition
            ? infoLineJackpotConfigPosition
            : position
    const winLineWrapper = useConfigurationStore((state) => state.getComponentConfig('InfoLineWinLineWrapper').styles)
    const winAmountStyles = useConfigurationStore((state) => state.getComponentConfig('InfoLineWinAmount').styles)
    const lineNumberStyles = useConfigurationStore((state) => state.getComponentConfig('InfoLineNumber').styles)
    const textStyles = useConfigurationStore((state) => state.getComponentConfig('InfoLineText').styles)
    const imageStyles = useConfigurationStore((state) => state.getComponentConfig('InfoLineImageWrapper').styles)
    const imageDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('InfoLineImageWrapper').dimensions
    )
    const freespinStyles = useConfigurationStore((state) => state.getComponentConfig('InfoLineFreespinText').styles)

    const infoLineComponent = useRef<HTMLDivElement | null>(null)
    // Default order: Text, Images and Win amount
    const [layout, setLayout] = useState([
        InfoLineLayoutElements.TEXT,
        InfoLineLayoutElements.IMAGES,
        InfoLineLayoutElements.WIN_AMOUNT,
    ])

    useOpacityToggle(ChangeOpacityComponents.INFO_LINE, infoLineComponent)

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const { type, scope } = event.data

            if (scope !== GameUIMessageScope.INFO_LINE) {
                return
            }

            const typeBasedActions: Partial<Record<GameUIMessageType, () => void>> = {
                [GameUIMessageType.SETUP_INFO_LINE_LAYOUT]: () => {
                    const data = rawData as SetupInfoLineLayoutMessage

                    // Expects array with the order of how the elements should appear in the InfoLine
                    setLayout(data.layout)
                },
                [GameUIMessageType.INFO_LINE_SEND_WINNING_COMBINATION]: () => {
                    const data = rawData as InfoLineSendWinningCombinationMessage

                    updateWinLineData(data.winLineData)
                },
                [GameUIMessageType.SHOW_INFO_LINE_TEXT]: () => {
                    const data = rawData as ShowInfoLineTextMessage
                    const infoLineComponentRef = infoLineComponent.current

                    if (data.text) {
                        const infoLineText = translate(data.text as TranslationKey)

                        if (data.shouldFadeIn && infoLineComponentRef) {
                            fadeGSAPAnimation(false, [infoLineComponentRef], 0.3).then(() => {
                                updateText(infoLineText!)

                                return fadeGSAPAnimation(true, [infoLineComponentRef], 0.3)
                            })
                        } else {
                            updateText(infoLineText)
                        }
                    }

                    updateTextVisibility(true)
                },
                [GameUIMessageType.HIDE_INFO_LINE_TEXT]: () => {
                    updateWinLineData(null)
                    updateTextVisibility(false)
                },
                [GameUIMessageType.INFO_LINE_SEND_ATLAS_FRAME_MAP]: () => {
                    const data = rawData as InfoLineAtlasMapMessage

                    addAtlasMap(data.atlasMap)

                    if (data.atlasTexturesData) {
                        handleAtlasTexturesData(data.atlasTexturesData)
                    } else {
                        preloadImages(Object.keys(data.atlasMap))
                    }
                },
                [GameUIMessageType.FREESPIN_SHOW_INFO_LINE]: () => {
                    setIsFreespinFullscreen(false)
                },
                [GameUIMessageType.FREESPIN_HIDE_INFO_LINE]: () => {
                    setIsFreespinFullscreen(true)
                },
                [GameUIMessageType.INFO_LINE_UPDATE_FREESPIN_COUNT]: () => {
                    const data = rawData as InfoLineUpdateFreespinCountMessage

                    updateFreespinText(data.freespinText ?? '')
                    updateCurrentFreespin(data.currentFreespin)
                    updateTotalFreespins(data.totalFreespins)
                },
                [GameUIMessageType.INFO_LINE_START_FREESPIN]: () => {
                    updateCurrentFreespin(0)
                    updateTotalFreespins(0)
                    setIsFreespinsActive(true)
                },
                [GameUIMessageType.INFO_LINE_END_FREESPIN]: () => {
                    updateCurrentFreespin(0)
                    updateTotalFreespins(0)
                    setIsFreespinsActive(false)
                },
            }

            typeBasedActions[type as GameUIMessageType]?.()

            const activeStates = [SpinButtonState.SEND_IDLE_STATE].includes(type)

            if (activeStates) {
                updateWinLineData(null)
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    }, [])

    const renderFreespinLinesContent = () => {
        return (
            <div id="info-line-freespins" className={freespinStyles}>
                {freespinText ? freespinText : t('gameui.bonusSystem.freeSpin', undefined, 1)}
                {'\n'} <p className="text-orange-300 inline mr-[5px]">{currentFreespin}</p>
                {!shouldHideTotalFreespinsText && (
                    <>
                        {` ${t('gameui.of')} `}
                        <p className="text-orange-300 inline ml-[5px]">{totalFreespins}</p>
                    </>
                )}
            </div>
        )
    }

    const renderText = (winCombination?: WinLineCombinationData, idSuffix = '') => {
        if (isTextVisible) {
            return (
                <div id={`info-line-text${idSuffix}`} className={textStyles}>
                    {text}
                </div>
            )
        }

        if (!winCombination) return

        const { lineNumber, cardsCount, text: winLineText } = winCombination
        const defaultWinLineDataText = `${lineNumber === -1 ? 'Scatter ' : `${t('gameui.line', undefined, 1)} ${lineNumber} `}${cardsCount}x`

        return (
            <div id={`info-line-text${idSuffix}`} className={`${lineNumberStyles} whitespace-nowrap`}>
                {winLineText ?? defaultWinLineDataText}
            </div>
        )
    }

    const renderImages = (winCombination: WinLineCombinationData, idSuffix = '') => {
        const { card, imageName, numberOfImages } = winCombination

        // If numberOfImages specifically set to 0 do not continue
        // If undefined we fallback to default layout to render one image
        if (numberOfImages === 0) return

        let imageData: { atlasName: string; value: AtlasMapValue } | undefined

        if (imageName) {
            imageData = findElementByImageName(imageName)
        } else if (card !== undefined) {
            const formattedCard = card?.toString().padStart(2, '0')

            const smallImagePrefix = 's'
            const eqReelImagePrefix = 'eq'
            const prefix = isJackpotEQRunning ? eqReelImagePrefix : smallImagePrefix

            imageData = findElementByImageName(`${prefix}${formattedCard}.png`)

            if (!imageData) imageData = findElementByImageName(`${formattedCard}.png`)
        }

        if (!imageData) {
            return
        }

        const imageHeight = Number((imageDimensions?.height ?? '0').replace('px', ''))
        const scale = imageHeight / imageData.value.height

        // Fallback to one image
        return (
            <div id={`info-line-images${idSuffix}`} className="flex gap-[2px]">
                {Array.from({ length: numberOfImages ?? 1 }).map((_, i) => (
                    <div id={`info-line-image${i}`} key={i} className={imageStyles}>
                        <div
                            id="info-line-image-style"
                            style={{
                                ...buildAtlasImageStyles(
                                    imageData.atlasName,
                                    imageData.value,
                                    cachedImages[imageData.atlasName],
                                    scale
                                ),
                                flex: '0 0 auto',
                            }}
                        />
                    </div>
                ))}
            </div>
        )
    }

    const getFormattedWinAmount = (winCombination: WinLineCombinationData) => {
        const { lineWinAmount } = winCombination

        return formatMoney(lineWinAmount, currencyHideCents)
    }

    const renderWinAmount = (winCombination: WinLineCombinationData, idSuffix = '') => {
        const { showLinesWin, hideEquals } = winCombination

        return (
            showLinesWin && (
                <div id={`info-line-win-amount${idSuffix}`} className={`${winAmountStyles} whitespace-nowrap`}>
                    {hideEquals ? '' : '='} {getFormattedWinAmount(winCombination)}
                    <p className="ml-[5px]">{currency}</p>
                </div>
            )
        )
    }

    const renderWinLinesWrapper = (
        children: (React.JSX.Element | React.JSX.Element[] | undefined | boolean)[],
        idSuffix = ''
    ) => {
        if (!hasWinLineData) return

        const { lineNumber, text: customWinLineText } = winLineData // Take data from default win line data since extra combos do not wrap
        const wrapTextToFit =
            !fitInfoInOneLine &&
            !hasMultipleWinLines && // Do not wrap to fit if has extra win line combinations because there isn't enough space
            viewport === ViewportType.MOBILE_PORTRAIT && // Only in mobile
            ((lineNumber === -1 && getFormattedWinAmount(winLineData).length >= 6) || // If scatter and amount is longer than 5 characters
                (winLineData.numberOfImages ?? 1) > 1 || // or if we have multiple images
                getFormattedWinAmount(winLineData).length >= 10 || // or in any case amount/text is longer than 9 characters
                (customWinLineText?.length ?? 0) >= 10)

        let gap = undefined

        if (wrapTextToFit) gap = '4px'
        else if (hasMultipleWinLines) {
            if (keepWinLinesSeparate) gap = '0px'
            else gap = '6px'
        }

        return (
            <div
                id={`info-line-win-lines-wrapper${idSuffix}`}
                className={winLineWrapper}
                style={{
                    width: hasMultipleWinLines ? 'auto' : undefined,
                    flexDirection: wrapTextToFit ? 'column' : 'row',
                    flexWrap: hasMultipleWinLines && !keepWinLinesSeparate ? 'wrap' : undefined,
                    gap,
                }}
            >
                {children}
            </div>
        )
    }

    const renderElements = (winCombination?: WinLineCombinationData, idSuffix = '') => {
        const elements = layout.map((el) => {
            switch (el) {
                case InfoLineLayoutElements.TEXT: {
                    return renderText(winCombination, idSuffix)
                }
                case InfoLineLayoutElements.IMAGES: {
                    return winCombination ? renderImages(winCombination, idSuffix) : undefined
                }
                case InfoLineLayoutElements.WIN_AMOUNT: {
                    return winCombination ? renderWinAmount(winCombination, idSuffix) : undefined
                }
                default: {
                    return
                }
            }
        })

        if (!isTextVisible && hasWinLineData) return renderWinLinesWrapper(elements, idSuffix)

        return elements
    }

    const renderAccumulatedWinLine = () => {
        // Layout: [cardsCount1]x [image1] + [cardsCount2]x [image2] + [cardsCount3]x [image3] = [winAmount] [currency]
        const elements = []
        let summedWinAmount = 0

        for (const [idSuffix, combo] of Object.entries(winLineCombosArray)) {
            elements.push(
                renderText(combo, idSuffix),
                renderImages(combo, idSuffix),
                <div id={`info-line-plus${idSuffix}`} className={`${lineNumberStyles} whitespace-nowrap`}>
                    +
                </div>
            )
            summedWinAmount = summedWinAmount + combo.lineWinAmount
        }

        elements.pop() // remove the last + to show the = [winAmount]

        elements.push(renderWinAmount({ ...winLineCombosArray[0], lineWinAmount: summedWinAmount }))

        return <div id="info-line-accumulated-win">{renderWinLinesWrapper(elements)}</div>
    }

    const renderInfoLinesContent = () => {
        const lines = []

        if (!isTextVisible && winLineCombosArray.length > 0) {
            if (!keepWinLinesSeparate) {
                // Since more than 2 win lines might not fit we need to render an accumulation of the whole win
                lines.push(renderAccumulatedWinLine())
            } else {
                for (const [idSuffix, combo] of Object.entries(winLineCombosArray)) {
                    lines.push(renderElements(combo, idSuffix))
                }
            }
        } else {
            lines.push(renderElements())
        }

        if (!fitInfoInOneLine) {
            // Align the win lines to the left while still centering the whole content in the info line area
            return <div className="flex flex-col items-start">{lines}</div>
        }

        return lines
    }

    return (
        <div
            id="info-line"
            ref={infoLineComponent}
            className={`${styles} ${currentFreespin ? 'bg-freespin-winline-gradient' : ''} ${hideInfoLine ? 'hidden' : ''}`}
            style={{
                ...infoLinePosition,
                backgroundImage: isJackpotRoundRunning ? 'unset' : undefined,
                gap: hasMultipleWinLines && fitInfoInOneLine ? '16px' : undefined,
            }}
        >
            {!!currentFreespin && !hasWinLineData && !isTextVisible
                ? renderFreespinLinesContent()
                : renderInfoLinesContent()}
        </div>
    )
}

export default InfoLine
